import cn from 'classnames';
import CountUp from 'react-countup';
import { ChangeEvent, useCallback, useContext, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { toast } from 'react-toastify';
import { useMutation } from '@apollo/client';
import { SocketContext } from '@context/socket';
import { BET_JACKPOT_GAME, JOIN_JACKPOT_GAME } from '@graphql/Mutations';
import { IJackpotPlayerObject } from '@api/types/jackpot';
import { ReactComponent as CoinIcon } from '@assets/svg/coin.svg';
import { getUserIdFromToken, toastErrorMessage } from '@utils/utils';
import BetInput from '@components/bet-input';
import Button from '@components/button';
import InfoCard from '@components/info-card';
import Labeler from '@components/labeler';
import CountdownTimer from '@components/countdown-timer';
import useReduxState from '@hooks/useReduxState';
import useSound from 'use-sound';
import readyAudio from '@assets/audio/ready.mp3';
import errorAudio from '@assets/audio/error.mp3';
import styles from './game.module.scss';
import useJackpotRoom from '@hooks/useJackpotRoom';

interface IGameState {
    stake: number;
    chance: number;
    userId: string;
}

const StartingGame = (): JSX.Element => {
    const socket: Socket = useContext(SocketContext);
    const [{ jackpot, userData: user, chat: chatState }] = useReduxState(
        (state) => state,
    );
    const { rooms } = jackpot;
    const { currentRoom } = useJackpotRoom();
    const [isBetting, setIsBeting] = useState<boolean>(false);
    const [joinGame] = useMutation(JOIN_JACKPOT_GAME);
    const [betInGame] = useMutation(BET_JACKPOT_GAME);

    const [playReadySound] = useSound(readyAudio, { interrupt: true });
    const [playErrorSound] = useSound(errorAudio, { interrupt: true });

    const {
        max_stake,
        min_stake,
        max_stakes_count,
        max_stakes_per_user,
        current_game: game,
    } = rooms[currentRoom];

    const time =
        25 -
        (game.game_metadata.started_at
            ? Math.floor(Date.now() / 1000) - game.game_metadata.started_at
            : 0);

    const toggleIsBetting = useCallback(
        (state: boolean) => {
            setIsBeting(state);
        },
        [currentRoom],
    );

    const handleBet = (): void => {
        if (!isBetValid()) {
            setGameState((prevState) => ({ ...prevState, stake: min_stake }));
            playErrorSound();

            return;
        }

        game.players.map((p) => p.user.id).includes(user.id)
            ? execute(betInGame)
            : execute(joinGame);
    };

    const execute = async (
        action: typeof joinGame | typeof betInGame,
    ): Promise<void> => {
        try {
            if (!isBetting) {
                toggleIsBetting(true);
                await action({
                    variables: {
                        userId,
                        gameUid: game.uid,
                        stake,
                    },
                });
                socket.emit('Join-Jackpot');
                toast.success('Bet Placed Successfully!');

                playReadySound();
            }
        } catch (err: any) {
            toastErrorMessage(err.message);
            playErrorSound();
        } finally {
            toggleIsBetting(false);
        }
    };

    /* Might be undefined if player is not in the game*/
    const currentPlayer: IJackpotPlayerObject | undefined = game.players.find(
        (player) => player.user.id === user.id,
    );

    const calcWinningChance = (stake: number): number => {
        if (game.bank > 0)
            return currentPlayer
                ? Number(
                      ((currentPlayer.total_stake / game.bank) * 100).toFixed(
                          1,
                      ),
                  )
                : Number(((stake / game.bank) * 100).toFixed(1));
        else return stake > 0 ? 100 : 0;
    };

    const [{ stake, chance, userId }, setGameState] = useState<IGameState>({
        stake: min_stake,
        chance: calcWinningChance(min_stake * 100),
        userId: getUserIdFromToken(),
    });

    const saveBetAmount = ({
        target: { value },
    }: ChangeEvent<HTMLInputElement>): void =>
        setGameState((prevState) => {
            const stake = Number(value);

            return stake < 0
                ? prevState
                : {
                      ...prevState,
                      chance: calcWinningChance(stake * 100),
                      stake,
                  };
        });

    const isBetValid = (): boolean => {
        (!userId || !user.id) && toast.error('Connect a wallet to place a bet');
        stake < min_stake && toast.error(`Minimum stake is ${min_stake}`);
        stake > max_stake && toast.error(`Maximum stake is ${max_stake}`);

        const baseReturn =
            stake >= min_stake &&
            stake <= max_stake &&
            Boolean(userId) &&
            Boolean(user.id);

        /* Won't be a current player if haven't joined to the game */
        if (!currentPlayer) return baseReturn;

        currentPlayer.total_stakes === max_stakes_per_user &&
            toast.error(`Maximum stakes per user are ${max_stakes_per_user}`);
        stake > user.balance &&
            toast.error(`Your balance is not enough to bet ${stake}`);

        return (
            baseReturn &&
            currentPlayer.total_stakes !== max_stakes_per_user &&
            stake <= user.balance
        );
    };

    const getJackpotBarWidth = (): { width: string } => ({
        width: `${(game.total_stakes / 50) * 100}%`,
    });

    /* ClassNames */

    const containerClasses: string = cn(styles.game, 'panel-container', {
        [styles.chatIsVisible]: !chatState.collapsed,
    });

    return (
        <section className={containerClasses}>
            <div className={cn(styles.info, 'upper-section')}>
                <Labeler
                    label="Total jackpot"
                    className={styles.jackpotbar_label}
                >
                    <div className={styles.jackpotbar}>
                        <div className={styles.jackpotbar__bank}>
                            <CoinIcon />
                            <span>
                                <CountUp end={game.bank / 100} duration={1} />
                            </span>
                        </div>
                        <div
                            className={styles.jackpotbar__bar}
                            style={getJackpotBarWidth()}
                        />
                        <span>
                            <CountUp end={game.total_stakes} duration={0.5} />/
                            {max_stakes_count}
                        </span>
                    </div>
                </Labeler>
                <Labeler
                    label={
                        game.game_metadata.status === 'started'
                            ? 'Starting in'
                            : 'Time'
                    }
                >
                    <CountdownTimer
                        seconds={time > 0 ? time : 0}
                        start={game.game_metadata.status === 'started'}
                        className={styles.info__timer}
                        startingDate={game.game_metadata.started_at}
                    />
                </Labeler>
            </div>
            <div className={cn(styles.betsection, 'lower-section')}>
                <div className={styles.betsection__inputs}>
                    <Labeler label="Bet">
                        <BetInput
                            min={min_stake.toString()}
                            max={max_stake.toString()}
                            step="50"
                            onChange={saveBetAmount}
                            onEnterDown={handleBet}
                            value={stake.toString()}
                            icon={<CoinIcon />}
                            className={styles.betsection__bet}
                        />
                    </Labeler>
                    <Labeler label="Chance">
                        <InfoCard
                            color={
                                chance <= 0
                                    ? 'dark-gradient'
                                    : 'yellow-gradient'
                            }
                            className={styles.betsection__chance}
                            text={`${chance}%`}
                        />
                    </Labeler>
                </div>
                <Button
                    color="green"
                    onClick={handleBet}
                    size="normal"
                    radius="pill"
                >
                    Place a bet
                </Button>
            </div>
        </section>
    );
};

export default StartingGame;
